import { type ReactNode } from 'react'
import { resolveCardTheme, type CardSlug } from '@terros/common'
import { Group, Paper, SegmentedControl, Stack, Text } from '@mantine/core'
import { Theme, useTranslation } from '@front/model'
import { MaterialSymbolIcon, TButton } from '@components'
import { ScaledPreview } from './preview/ScaledPreview'
import {
  isPreviewLayout,
  previewDesignWidth,
  type PreviewLayout,
  useCardPreviewContent,
} from './preview/previewContent'
import { MobileCardPreview } from './preview/MobileCardPreview'
import { DesktopCardPreview } from './preview/DesktopCardPreview'
import { type CompanyCardFormValues } from './components/CompanyCardFields'

type Props = {
  companyName: string
  card: CompanyCardFormValues
  gallerySlug?: CardSlug
  repAvatarUrl: string
  layout: PreviewLayout
  onLayoutChange: (layout: PreviewLayout) => void
  onExpand?: () => void
}

export const CardLivePreview = ({
  companyName,
  card,
  gallerySlug,
  repAvatarUrl,
  layout,
  onLayoutChange,
  onExpand,
}: Props): ReactNode => {
  const { t } = useTranslation()
  const tokens = resolveCardTheme(card.theme)
  const content = useCardPreviewContent({ companyName, repAvatarUrl, card, gallerySlug })

  const changeLayout = (value: string): void => {
    if (isPreviewLayout(value)) onLayoutChange(value)
  }

  return (
    <Stack gap='xs'>
      <Group justify='space-between'>
        <SegmentedControl
          aria-label={t('Preview layout')}
          data={[
            { value: 'mobile', label: t('Mobile') },
            { value: 'desktop', label: t('Desktop') },
          ]}
          size='xs'
          value={layout}
          onChange={changeLayout}
        />
        {onExpand && (
          <TButton
            buttonType='icon-rounded'
            icon='open_in_full'
            size='small'
            tooltip={t('Expand preview')}
            variant='ghost'
            onClick={onExpand}
          />
        )}
      </Group>
      <Paper
        data-mantine-color-scheme='light'
        bg={Theme.light.background.neutral.primary}
        bd={`1px solid ${Theme.light.border.neutral.primary}`}
        p='xs'
        radius='lg'
        style={{ overflow: 'hidden' }}
      >
        <ScaledPreview designWidth={previewDesignWidth(layout)}>
          {layout === 'mobile' ? (
            <MobileCardPreview content={content} tokens={tokens} />
          ) : (
            <DesktopCardPreview content={content} tokens={tokens} />
          )}
        </ScaledPreview>
      </Paper>
      <Group gap='xs'>
        <Text c='green'>
          <MaterialSymbolIcon icon='lock' size='sm' />
        </Text>
        <Text c='dimmed' ff='monospace' size='xs'>
          card.terros.com/{card.slug || t('your-card')}
        </Text>
      </Group>
    </Stack>
  )
}
